import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { Avatar } from "./Avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, PawPrint, MessageSquare, UserPlus, MessageCircle, ShieldCheck, Heart } from "lucide-react";

function iconFor(type) {
  if (type === "reaction") return <PawPrint className="w-4 h-4 text-spotter-gold" />;
  if (type === "comment") return <MessageSquare className="w-4 h-4 text-spotter-maroon" />;
  if (type === "friend_request" || type === "friend_accept") return <UserPlus className="w-4 h-4 text-spotter-maroon" />;
  if (type === "message") return <MessageCircle className="w-4 h-4 text-blue-600" />;
  if (type === "guardian_request") return <ShieldCheck className="w-4 h-4 text-spotter-maroon" />;
  return <Heart className="w-4 h-4 text-spotter-maroon" />;
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const rt = useRealtime();

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data.notifications);
    rt.setUnread(data.unread);
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) load();
  };

  const clickItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) {}
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      rt.setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    rt.setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative p-2 rounded-lg text-spotter-cream hover:bg-white/15">
          <Bell className="w-6 h-6" />
          {rt.unread > 0 && (
            <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[11px] font-bold rounded-full bg-spotter-gold text-black border border-black flex items-center justify-center">{rt.unread > 99 ? "99+" : rt.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 border-2 border-black bg-white">
        <div className="flex items-center justify-between p-3 border-b-2 border-black bg-spotter-cream">
          <span className="font-heading font-bold">Spot Alerts</span>
          <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-spotter-maroon font-semibold hover:underline">Mark all read</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-spotter-maroon">No notifications yet.</div>}
          {items.map((n) => (
            <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => clickItem(n)}
              className={`w-full flex items-start gap-2 p-3 text-left border-b hover:bg-spotter-cream ${n.read ? "opacity-60" : "bg-spotter-gold/10"}`}>
              <Avatar user={{ name: n.actor_name, avatar_url: n.actor_avatar }} size="sm" />
              <div className="flex-1">
                <div className="text-sm"><b>{n.actor_name}</b> {n.message}</div>
                <div className="text-xs text-spotter-maroon mt-0.5 flex items-center gap-1">{iconFor(n.type)} {new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-spotter-gold mt-2" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
